import { Paper } from '@mui/material';
import { darken } from '@mui/system';
import { GridRowClassNameParams, GridSortModel } from '@mui/x-data-grid';
import { Order } from '@thro-types/leihbar';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useOrdersQuery } from 'api/hooks/leihbar';
import { DataGrid, TypedGridColumns } from 'app/components';
import { translations } from 'locales/i18n';
import { Colors } from 'utils/colors';
import { formatToDateAndTime, formatToDateWithoutTime } from 'utils/dates';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import '../Orders/styles/Orders.scss';
import { getOrderRowClassName } from '../helper';
import { DailyView } from '../leihbar.types';

interface Props {
  filters: {
    search?: string;
    startDay?: string;
    endDay?: string;
    withConflicts?: boolean;
    view?: DailyView;
    lenderGroupId?: number | null;
  };
  daily?: boolean;
}

export const OrderDatagrid = ({ filters, daily }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentURL = useCurrentURL();

  const [sortingmodel, setSortingModel] = useState<GridSortModel>([
    daily ? { field: 'urgentDate', sort: 'asc' } : { field: 'createdAt', sort: 'desc' }
  ]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const { data: orders, isLoading } = useOrdersQuery({
    params: {
      ...filters,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
      [sortingmodel[0]?.field]: sortingmodel[0]?.sort
    }
  });

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const getRowClassName = (params: GridRowClassNameParams<Order>) => {
    if (!daily) return '';
    return getOrderRowClassName(params.row);
  };

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortingModel(model);
  }, []);

  const getMostUrgentBookingDate = (order: Order, view?: DailyView) => {
    const relevantTime = view === 'pickups' ? 'startTime' : 'endTime';

    const mostUrgentBooking = order.bookings
      ?.filter(booking => (!booking.deletedAt && view === 'pickups' ? !booking.collectedAt : true))
      .sort((a, b) => {
        return new Date(a[relevantTime]).getTime() - new Date(b[relevantTime]).getTime();
      })[0];

    if (!mostUrgentBooking) {
      return '';
    }

    return formatToDateAndTime(mostUrgentBooking[relevantTime]);
  };

  const columns: TypedGridColumns<Order & { articleCount: number; urgentDate: Date }> = [
    { field: 'id', headerName: t(translations.common.id()), flex: 1 },
    {
      field: 'name', // TODO Add email link
      headerName: t(translations.common.name()),
      flex: 2,
      renderCell: params =>
        params.row.name ? (
          <a
            href={`mailto:${params.row.email}`}
            onClick={e => e.stopPropagation()}
            className="link"
          >
            {params.row.name}
          </a>
        ) : (
          '-'
        )
    },
    {
      field: 'articleCount',
      headerName: t(translations.pages.leihbar.orders.columns.articleCount()),
      flex: 2,
      renderCell: params =>
        `${params.row.bookings?.length} ${t(translations.pages.leihbar.orders.articleInOrder())}`
    },
    {
      field: 'urgentDate',
      headerName:
        filters.view === 'pickups'
          ? t(translations.pages.leihbar.orders.columns.pickupDate())
          : t(translations.pages.leihbar.orders.columns.returnDate()),
      flex: 1,
      renderCell: params => getMostUrgentBookingDate(params.row, filters.view),
      hideable: false
    },
    {
      field: 'createdAt',
      headerName: t(translations.pages.leihbar.orders.orderDate()),
      flex: 1,
      renderCell: params => formatToDateWithoutTime(params.row.createdAt),
      hideable: false
    }
  ];

  /**
   * Filter orders for daily dashboard.
   *
   * @returns {Array} An array of orders that match the filtering criteria.
   */
  const getFilteredOrdersForDaily = (): Order[] => {
    return (
      orders?.data.filter(order => {
        return order.bookings?.some(booking => {
          return (
            !booking.deletedAt &&
            ((booking.state === 'COLLECTED' && !booking.returnedAt) ||
              booking.state === 'BOOKED' ||
              booking.state === 'PICKED')
          );
        });
      }) || []
    );
  };

  return (
    <Paper sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        columns={columns}
        rows={daily ? getFilteredOrdersForDaily() : orders?.data || []}
        style={{ height: '100%', width: '100%' }}
        onRowClick={({ id }) => {
          if (daily) {
            navigate(`/leihbar/daily/${id}`, { state: { back: currentURL } });
          } else {
            navigate(`/leihbar/orders/${id}`, { state: { back: currentURL } });
          }
        }}
        loading={isLoading}
        paginationMode="server"
        onPaginationModelChange={handlePaginationChange}
        paginationModel={{
          page: paginationModel.page,
          pageSize: paginationModel.pageSize
        }}
        localeText={{ noRowsLabel: t(translations.pages.leihbar.daily.noBookings()) }}
        rowCount={orders?.count || 0}
        sortingMode="server"
        filterMode="server"
        onSortModelChange={handleSortModelChange}
        getRowClassName={getRowClassName}
        initialState={{
          columns: {
            columnVisibilityModel: {
              urgentDate: daily ?? false,
              createdAt: !daily
            }
          }
        }}
        sx={{
          '& .overdue': {
            backgroundColor: Colors.leihbarBookingOverdueColorLight,
            borderLeftColor: Colors.leihbarBookingOverdueColor,
            borderLeftWidth: '5px',
            borderLeftStyle: 'solid',
            borderTopLeftRadius: '4px',
            borderBottomLeftRadius: '4px',
            '&:hover': {
              backgroundColor: darken(Colors.leihbarBookingOverdueColorLight, 0.05)
            }
          },
          '& .conflicting': {
            backgroundColor: Colors.leihbarBookingConflictingColorLight,
            borderLeftColor: Colors.leihbarBookingConflictingColor,
            borderLeftWidth: '5px',
            borderLeftStyle: 'solid',
            borderTopLeftRadius: '4px',
            borderBottomLeftRadius: '4px',
            '&:hover': {
              backgroundColor: darken(Colors.leihbarBookingConflictingColorLight, 0.05)
            }
          }
        }}
      />
    </Paper>
  );
};
